/* ************************************************************************** */
/*                                Dependencies                                */
/* ************************************************************************** */

// Lib dependencies
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import upperFirst from 'lodash/upperFirst';

// UI lib components
import { Select, Text } from '@mantine/core';

// Lib types
import {
  ComboboxItem,
  ComboboxLikeRenderOptionInput,
  SelectProps,
} from '@mantine/core';

// Local Types & API
import { QuestionPriorityOption, QuestionPrioritySelectProps } from './type';
import { QuestionPriority } from '@app/types/blackroom-api';

// Styles
import { palette } from '@app/shared/ui-kit/colors';

/* ************************************************************************** */
/*                                  Component                                 */
/* ************************************************************************** */

function QuestionPrioritySelect({
  selectedPriority,
  ...props
}: QuestionPrioritySelectProps) {
  /* ********************************** HOOKS ********************************* */

  // Localization
  const { t } = useTranslation(['Support']);

  const questionPriorityOptions: QuestionPriorityOption[] = useMemo(
    () => [
      {
        label: upperFirst(t('Support:Common.priorities.low')),
        color: palette.blackroomGreen,
        value: QuestionPriority.Low,
      },
      {
        label: upperFirst(t('Support:Common.priorities.medium')),
        color: palette.blackroomOrange,
        value: QuestionPriority.Medium,
      },
      {
        label: upperFirst(t('Support:Common.priorities.high')),
        color: palette.blackroomRed,
        value: QuestionPriority.High,
      },
    ],
    [t],
  );

  /* ******************************** CALLBACKS ******************************* */

  const renderQuestionPriorityOption: SelectProps['renderOption'] = useCallback(
    ({ option }: ComboboxLikeRenderOptionInput<ComboboxItem>) => (
      <Text style={{ color: (option as QuestionPriorityOption).color }}>
        {option.label}
      </Text>
    ),
    [],
  );

  /* ******************************** RENDERING ******************************* */
  return (
    <Select
      defaultValue={selectedPriority}
      data={questionPriorityOptions}
      renderOption={renderQuestionPriorityOption}
      styles={{
        input: {
          color: questionPriorityOptions.find(
            (option) => option.value === selectedPriority,
          )?.color,
        },
      }}
      {...props}
    />
  );
}

export default QuestionPrioritySelect;
